import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CBadge } from "@coreui/react-pro";
import { AppSidebar as SharedAppSidebar, type RenderNavLink } from "@scryerlabs/ui";
import { routePaths } from "../../routes/route-paths";
import { navSections } from "./nav-config";
import {
  useMembershipsEnabled,
  usePortfolioBuilderEnabled,
} from "../../api/hooks/usePortalSettings";

interface AppSidebarProps {
  visible: boolean;
  onVisibleChange: (visible: boolean) => void;
  unfoldable: boolean;
  onUnfoldableChange: (unfoldable: boolean) => void;
}

/**
 * Routing stays here. @scryerlabs/ui takes a render prop rather than importing
 * react-router, so the package pins no router version for its consumers - which matters
 * because the brochure and the two offices are not obliged to move together.
 */
const renderNavLink: RenderNavLink = ({ href, className, end, children }) => (
  <NavLink
    to={href}
    end={end}
    className={({ isActive }) => `${className}${isActive ? " active" : ""}`}
  >
    {children}
  </NavLink>
);

export function AppSidebar({
  visible,
  onVisibleChange,
  unfoldable,
  onUnfoldableChange,
}: AppSidebarProps) {
  const { t } = useTranslation();
  const membershipsEnabled = useMembershipsEnabled();
  const portfolioBuilderEnabled = usePortfolioBuilderEnabled();

  return (
    <SharedAppSidebar
      brandHref={routePaths.dashboard}
      renderNavLink={renderNavLink}
      visible={visible}
      onVisibleChange={onVisibleChange}
      unfoldable={unfoldable}
      onUnfoldableChange={onUnfoldableChange}
      // Nav data and any gating stay in the app. The package renders whatever it is
      // given, which is why the back office's much larger sidebar is the same component.
      sections={navSections.map((section) => ({
        key: section.key,
        title: section.titleKey ? t(`common:navigationSections.${section.titleKey}`) : undefined,
        items: section.items
          .filter((item) => membershipsEnabled || !item.membershipsOnly)
          .filter((item) => portfolioBuilderEnabled || !item.portfolioBuilderOnly)
          .map((item) => ({
            key: item.to,
            href: item.to,
            end: item.end,
            icon: item.icon as string | string[],
            label: item.badgeKey ? (
              <>
                <span className="me-2">{t(`common:navigation.${item.labelKey}`)}</span>
                <CBadge color="primary" shape="rounded-pill" className="ms-auto">
                  {t(`common:navigationBadges.${item.badgeKey}`)}
                </CBadge>
              </>
            ) : (
              t(`common:navigation.${item.labelKey}`)
            ),
          })),
      }))}
    />
  );
}
